"use client";

import { Row } from "@tanstack/react-table";
import {
  CheckCircle,
  ExternalLink,
  History,
  Lock,
  MoreHorizontal,
  Undo2,
  XCircle,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DivergenceStatusEnum } from "@/enum/divergence-status";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { AssignmentsEnum } from "@/enum/assignments";
import { useAssignmentAbilities } from "@/hooks/use-assignment-permission";
import { permissionDeniedLabel } from "@/constants/permission";
import { toast } from "@/components/ui/use-toast";
import { ConciliationItemTypeEnum } from "@/constants/conciliation-history";

import {
  CASE_TIMELINE_LABELS,
  CONCILIATION_LINK_LABELS,
  DECISION_LABELS,
} from "../../utils/messages";
import { PayrollDiscountCandidate } from "../../utils/is-payroll-discount";
import { DecisionStatus } from "../../utils/decision-consequence";
import { DecisionConfirmationModal } from "./decision-confirmation-modal";
import { CaseTimelineModal } from "./case-timeline-modal";
import { ReversalConfirmationModal } from "./reversal-confirmation-modal";
import { REVERSAL_LABELS, REVERSAL_TOASTS } from "../../utils/reversal-messages";
import { canReverseRow } from "../../utils/reversal-availability";
import { ReversibleSource, toReversible } from "../../utils/to-reversible";

export const CLOSED_PERIOD_LOCK_TEST_ID: string = "closed-period-lock";
export const REVERSAL_ACTION_TEST_ID: string = "reversal-action";

export interface DecidableDivergence extends PayrollDiscountCandidate {
  id: number;
  status?: DivergenceStatusEnum | null;
  closed_period?: boolean;
  reverted?: boolean;
  amount?: number;
  store_code?: number | string;
  conciliation_id?: number | null;
  conciliation?: { id?: number } | null;
  payroll_voucher?: {
    installments?: Array<{
      number: number;
      amount: number;
      competence: string;
      sent?: boolean;
    }>;
  } | null;
}

interface DataTableRowActionsProps {
  row: Row<DecidableDivergence>;
  openCycle?: string;
}

const DECIDABLE_STATUSES: readonly DivergenceStatusEnum[] = [
  DivergenceStatusEnum.PENDING,
  DivergenceStatusEnum.ACTIVE,
];

const MODAL_TITLE: Record<DecisionStatus, string> = {
  [DivergenceStatusEnum.APPROVED]: DECISION_LABELS.TITLE_APPROVE,
  [DivergenceStatusEnum.REJECTED]: DECISION_LABELS.TITLE_REJECT,
};

const MODAL_DESCRIPTION: Record<DecisionStatus, string> = {
  [DivergenceStatusEnum.APPROVED]: DECISION_LABELS.DESCRIPTION_APPROVE,
  [DivergenceStatusEnum.REJECTED]: DECISION_LABELS.DESCRIPTION_REJECT,
};

export function DataTableRowActions({
  row,
  openCycle,
}: DataTableRowActionsProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const { canUpdate } = useAssignmentAbilities(AssignmentsEnum.DIVERGENCE);
  const navigate = useNavigate();
  const location = useLocation();

  const divergence: DecidableDivergence = row.original;
  const isClosedPeriod: boolean = Boolean(divergence.closed_period);
  const canDecide: boolean =
    !isClosedPeriod &&
    DECIDABLE_STATUSES.includes(divergence.status as DivergenceStatusEnum);
  const canReverse: boolean = canReverseRow(divergence);
  const conciliationId: number | undefined =
    divergence.conciliation_id ?? divergence.conciliation?.id ?? undefined;

  const labelFor = (action: string): string =>
    canUpdate ? action : permissionDeniedLabel(action);

  const CLOSED_PERIOD_LABEL: Record<string, string> = {
    reversible: DECISION_LABELS.CLOSED_PERIOD,
    locked: DECISION_LABELS.CLOSED_PERIOD_NO_ACTION,
  };
  const closedPeriodLabel: string = CLOSED_PERIOD_LABEL[canReverse ? "reversible" : "locked"];

  const openDecision = (action: DecisionStatus): void => {
    handleModal({
      isOpen: true,
      title: MODAL_TITLE[action],
      description: MODAL_DESCRIPTION[action],
      element: (
        <DecisionConfirmationModal
          divergenceId={divergence.id}
          divergence={divergence}
          action={action}
        />
      ),
    });
  };

  const openHistory = (): void => {
    handleModal({
      isOpen: true,
      title: CASE_TIMELINE_LABELS.TITLE,
      description: CASE_TIMELINE_LABELS.DESCRIPTION,
      element: (
        <CaseTimelineModal
          itemType={ConciliationItemTypeEnum.DIVERGENCE}
          itemId={divergence.id}
        />
      ),
    });
  };

  const openReversal = (): void => {
    if (!openCycle) {
      toast(REVERSAL_TOASTS.NO_OPEN_CYCLE);
      return;
    }

    handleModal({
      isOpen: true,
      title: REVERSAL_LABELS.TITLE,
      description: REVERSAL_LABELS.DESCRIPTION,
      element: (
        <ReversalConfirmationModal
          divergence={toReversible(divergence as ReversibleSource)}
          openCycle={openCycle}
        />
      ),
    });
  };

  const goToConciliation = (): void => {
    if (!conciliationId) return;
    navigate(
      `/conciliacao/lojas/conciliacao-de-caixa-de-loja/${conciliationId}`,
      { state: { from: location.pathname } },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          aria-label={DECISION_LABELS.MENU}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {canDecide && (
          <DropdownMenuItem
            disabled={!canUpdate}
            onClick={() => openDecision(DivergenceStatusEnum.APPROVED)}
          >
            <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
            {labelFor(DECISION_LABELS.APPROVE)}
          </DropdownMenuItem>
        )}

        {canDecide && (
          <DropdownMenuItem
            disabled={!canUpdate}
            onClick={() => openDecision(DivergenceStatusEnum.REJECTED)}
          >
            <XCircle className="mr-2 h-4 w-4 text-red-600" />
            {labelFor(DECISION_LABELS.REJECT)}
          </DropdownMenuItem>
        )}

        {canReverse && (
          <DropdownMenuItem
            disabled={!canUpdate}
            data-testid={REVERSAL_ACTION_TEST_ID}
            onClick={openReversal}
          >
            <Undo2 className="mr-2 h-4 w-4 text-amber-600" />
            {labelFor(REVERSAL_LABELS.MENU)}
          </DropdownMenuItem>
        )}

        {isClosedPeriod && (
          <DropdownMenuItem disabled data-testid={CLOSED_PERIOD_LOCK_TEST_ID}>
            <Lock className="mr-2 h-4 w-4 text-gray-400" />
            {closedPeriodLabel}
          </DropdownMenuItem>
        )}

        {divergence.reverted && (
          <DropdownMenuItem disabled>
            <Undo2 className="mr-2 h-4 w-4 text-gray-400" />
            {REVERSAL_LABELS.ALREADY_REVERTED}
          </DropdownMenuItem>
        )}

        {!canDecide &&
          !canReverse &&
          !isClosedPeriod &&
          !divergence.reverted &&
          Boolean(divergence.status) && (
            <DropdownMenuItem disabled>
              <XCircle className="mr-2 h-4 w-4 text-gray-400" />
              {DECISION_LABELS.ALREADY_DECIDED}
            </DropdownMenuItem>
          )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={openHistory}>
          <History className="mr-2 h-4 w-4" />
          {DECISION_LABELS.HISTORY}
        </DropdownMenuItem>

        {conciliationId && (
          <DropdownMenuItem onClick={goToConciliation}>
            <ExternalLink className="mr-2 h-4 w-4" />
            {CONCILIATION_LINK_LABELS.GO_TO}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
